import {
    useId,
    useMemo,
    useRef,
    useTransition,
    useState,
    type FC,
    type HTMLAttributeAnchorTarget,
} from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';

import { trackEvent } from 'dibs-tracking';
import { ScrollCarouselProvider } from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { InternalScrollCarouselProvider } from 'dibs-scroll-carousel/exports/InternalScrollCarouselContext.ts';
import { ProductCarousel } from './ProductCarousel.tsx';
import { ProductCarouselItem } from './ProductCarouselItem.tsx';
import {
    TILE_SIZES,
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from '../constants.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { type ProductImageCarousel_viewer$key } from './__generated__/ProductImageCarousel_viewer.graphql.ts';
import { type ProductImageCarouselRefetch_viewer$key } from './__generated__/ProductImageCarouselRefetch_viewer.graphql.ts';
import { type ProductImageCarousel_item$key } from './__generated__/ProductImageCarousel_item.graphql.ts';
import { type ProductImageCarouselRefetchQuery } from './__generated__/ProductImageCarouselRefetchQuery.graphql.ts';

const viewerFragment = graphql`
    fragment ProductImageCarousel_viewer on Viewer {
        ...ProductCarouselItem_viewer
        ...ProductImageCarouselRefetch_viewer @arguments(itemId: "", fetchPhotos: false)
    }
`;

const itemFragment = graphql`
    fragment ProductImageCarousel_item on Item
    @argumentDefinitions(
        fetchVideo: { type: "Boolean" }
        photosLimit: { type: "Int", defaultValue: 2 }
    ) {
        serviceId
        carouselPhotos: photos(limit: $photosLimit) {
            ...ProductCarouselItem_photos
        }
        ...ProductCarouselItem_item @arguments(fetchVideo: $fetchVideo)
    }
`;
const refetchableFragment = graphql`
    fragment ProductImageCarouselRefetch_viewer on Viewer
    @refetchable(queryName: "ProductImageCarouselRefetchQuery")
    @argumentDefinitions(
        itemId: { type: "String!" }
        fetchPhotos: { type: "Boolean", defaultValue: false }
    ) {
        photos(itemId: $itemId) @include(if: $fetchPhotos) {
            ...ProductCarouselItem_photos
        }
    }
`;

const trackArrowClick = ({
    nextPage,
    itemId,
}: {
    nextPage: number;
    itemId: string | null | undefined;
}): void => {
    trackEvent({
        category: 'results',
        action: 'results page image carousel arrow click',
        label: nextPage,
        additional: { itemId },
        isInteractiveEvent: true,
    });
};

const ITEMS_TO_SHOW = 1;

type Props = {
    viewer: ProductImageCarousel_viewer$key | null | undefined;
    item: ProductImageCarousel_item$key | null | undefined;
    buyerHost?: string;
    disableLinks?: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    imageLoadVerticalOffset?: number;
    index: number;
    itemsPerRow: number;
    isHovering: boolean;
    linkTarget?: HTMLAttributeAnchorTarget;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    size: TileSize;
    srcSet?: SrcSet;
    wasHovered: boolean;
    showSwipeIndicator?: boolean;
};

export const ProductImageCarousel: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    buyerHost,
    disableLinks,
    imageBackgroundColor,
    imageLoadVerticalOffset,
    index,
    itemsPerRow,
    isHovering,
    linkTarget,
    onClick,
    onContentLoaded,
    size,
    srcSet,
    wasHovered,
    showSwipeIndicator,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const [data, refetch] = useRefetchableFragment<
        ProductImageCarouselRefetchQuery,
        ProductImageCarouselRefetch_viewer$key
    >(refetchableFragment, viewer);
    const item = useFragment(itemFragment, itemRef);
    const imageId = useId();
    const [, startTransition] = useTransition();

    const { serviceId: itemId } = item || {};
    const carouselWasClicked = useRef(false);
    const [isSwiperDismissed, setIsSwiperDismissed] = useState(false);
    const photos = useMemo(
        () => (data?.photos || item?.carouselPhotos || []).filter(filterFalsy),
        [data, item]
    );

    const onPageChange = async (nextPage: number): Promise<void> => {
        trackArrowClick({ nextPage, itemId });

        if (!carouselWasClicked.current && itemId) {
            carouselWasClicked.current = true;
            startTransition(() => {
                refetch({ itemId, fetchPhotos: true });
            });
        }
    };

    const totalItems = photos.length;
    const showArrows = size !== TILE_SIZES.small && totalItems > 1;
    const showSwiper = !showArrows && !!showSwipeIndicator && totalItems > 1 && !isSwiperDismissed;
    return (
        <ScrollCarouselProvider
            totalItems={totalItems}
            isInfinite
            onSlideChange={({ activeSlide }) => {
                if (showSwiper) {
                    setIsSwiperDismissed(true);
                }
                onPageChange(activeSlide);
            }}
        >
            <InternalScrollCarouselProvider
                animation="slide"
                enableScroll={size === TILE_SIZES.small}
                orientation="horizontal"
                itemsToShow={ITEMS_TO_SHOW}
                snapAlign="center"
            >
                <ProductCarousel
                    size={size}
                    showArrows={showArrows}
                    showSwiper={showSwiper}
                    setIsSwiperDismissed={setIsSwiperDismissed}
                >
                    {photos.map((photo, photoIndex) => (
                        <ProductCarouselItem
                            key={`${imageId}-${photoIndex}`}
                            viewer={viewer}
                            item={item}
                            photo={photo}
                            buyerHost={buyerHost}
                            imageBackgroundColor={imageBackgroundColor}
                            disableLinks={disableLinks}
                            imageLoadVerticalOffset={imageLoadVerticalOffset}
                            isHovering={isHovering}
                            isEagerImageLoadEnabled={
                                index < itemsPerRow || wasHovered || carouselWasClicked.current
                            }
                            linkTarget={linkTarget}
                            onClick={onClick}
                            onContentLoaded={onContentLoaded}
                            photoIndex={photoIndex}
                            size={size}
                            srcSet={srcSet}
                        />
                    ))}
                </ProductCarousel>
            </InternalScrollCarouselProvider>
        </ScrollCarouselProvider>
    );
};
